'use client';

/**
 * Chat Dialog Provider
 *
 * Global context provider for controlling the chat dialog.
 * Allows opening the chat with specific context from anywhere in the app.
 */
import { type ReactNode, createContext, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EBActionContext } from '@lib/capabilities';
import { CHAT_SURFACE_ATTR } from '@lib/chat/constants/chat-surface-attr';
import { useOpenState } from '@lib/chat/panel/use-open-state';
import type { DealBuilderUpdateTarget } from '@lib/everything-bar/chat/types/deal-builder-update-target';
import { trackDealWizardLaunched } from '@lib/everything-bar/chat/utils/track-deal-wizard-events';
import type { WizardFlowId } from '@lib/everything-bar/chat/wizard/category-registry';
import { setChatWizardOpen } from '@lib/everything-bar/proactive-suggestions/chat-wizard-open-signal';
import type { MentionContext } from '@/lib/mentions';
import { getLogger } from '@/lib/observability/logging/log.client';
import type { ChatDialogContext, ContextQuickAction } from '../types/chat-dialog-context';
import type { DealBuilderState } from '../types/deal-wizard';
import type { ToastActionDescriptor } from '../types/toast-action-descriptor';
const {
  logInfo
} = getLogger('lib/everything-bar/chat/providers/chat-dialog-provider');

/** Idle time before an unpinned proactive card collapses. */
export const AUTO_COLLAPSE_MS = 25_000;

/**
 * DOM marker on every chat surface (minimized card, expanded
 * shell). Read by the collapse focus-restore guard to tell
 * whether the element losing focus was inside the chat.
 */

/**
 * Options for opening the deal builder.
 * Use `address` as a shorthand to pre-fill the property address
 * (triggers listing lookup and auto-fills downstream fields).
 */
export interface DealBuilderOpenOptions {
  initialState?: Partial<DealBuilderState>;
  /** Shorthand — sets initialState.address/formattedAddress and triggers listing pre-fill */
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    county?: string;
    country?: string;
  };
  /** Shorthand — formatted address string */
  formattedAddress?: string;
  /** Stable id of a custom wizard to run; omitted = built-in deal wizard. */
  wizardId?: string;
  /**
   * Which hardcoded flow to run — the deal wizard or the listing wizard.
   * Omitted = the deal wizard. Ignored when `wizardId` is set, since a custom
   * wizard brings its own category.
   */
  flow?: WizardFlowId;
  /**
   * Listings folder the created listing files into. Omitted files it at the
   * org listings root. Only meaningful for `flow: 'listing'`.
   */
  listingParentId?: string;
  /** Run the wizard against an existing deal and save the answers onto it. */
  target?: DealBuilderUpdateTarget;
}

/**
 * Options for opening the deal-document chat mode.
 * All fields are required -- the caller (doc-builder) has this data available.
 */
export interface DealDocumentOpenOptions {
  /** Submission ID */
  submissionId: string;
  /** Document ID in Document Engine */
  documentId: string;
  /** Collection ID */
  collectionId: string;
  /** Callback when AI populates fields via the populate_fields tool */
  onFieldUpdate: (updates: Array<{
    fieldName: string;
    value: string;
  }>) => void;
}

/** Target package for the signing-setup wizard. */
export interface SigningSetupOpenOptions {
  dealId: string;
  packageId: string;
}

/** One-shot launch behavior for flows that should restore a host surface. */
export interface SigningSetupLaunchOptions {
  /** Called instead of falling back to Ask Clai when the wizard Back exits. */
  onBack?: () => void;
}

/**
 * State for the chat dialog
 */
export interface ChatDialogState {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Whether the dialog is minimized to a pill */
  isMinimized: boolean;
  /** Current context (if any) */
  context: ChatDialogContext | null;
  /**
   * True while an auto-collapse timer is armed. Programmatic
   * composer focus would pin or cancel that timer, so autofocus
   * is suppressed for the open that armed it.
   */
  suppressComposerAutofocus: boolean;
  /** Optional file for document context */
  file: File | null;
  /** Initial message to send */
  initialMessage: string | null;
  /** Mentioned resources for AI */
  mentionsContext: MentionContext[];
  /** Chat mode: regular chat, deal builder, deal document, or signing setup */
  mode: 'chat' | 'deal-builder' | 'deal-document' | 'signing-setup';
  /** Deal builder options when mode is 'deal-builder' */
  dealBuilderOptions?: DealBuilderOpenOptions;
  /** Deal document options when mode is 'deal-document' */
  dealDocumentOptions?: DealDocumentOpenOptions;
  /** Signing setup options when mode is 'signing-setup' */
  signingSetupOptions?: SigningSetupOpenOptions;
  /** Greeting message displayed on the FAB bubble (null = compact mode) */
  greetingMessage: string | null;
  /** Whether clicking the FAB greeting card opens chat. Toast-style cues set this to false. */
  greetingOpensChat: boolean;
  /** Optional CTA shown alongside toast-style greeting copy. */
  toastAction: ToastActionDescriptor | null;
  /** Visual tone for the greeting/toast card. */
  greetingVariant: 'default' | 'success' | 'error';
  /** Text the user highlighted in the PDF — used as context for deal-document chat */
  selectionContext: {
    text: string;
    source: string;
  } | null;
  /**
   * Identifies one chat session. Only a wholesale open (open /
   * startDealBuilder / startSigningSetup) bumps it — drill-stack
   * ops and updateContext swap `context`'s identity without
   * starting a new session, so the session-reset effect keys off
   * this instead of `context`.
   */
  sessionEpoch: number;
}

/**
 * Options for opening the chat dialog
 */
export interface OpenChatOptions {
  /** Context to display */
  context?: ChatDialogContext;
  /** File to analyze */
  file?: File;
  /** Initial message to send immediately */
  initialMessage?: string;
  /** Resources mentioned in the chat */
  mentionsContext?: MentionContext[];
  /** Deal builder options to start in deal-builder mode */
  dealBuilderOptions?: DealBuilderOpenOptions;
  /** Deal document options to start in deal-document mode */
  dealDocumentOptions?: DealDocumentOpenOptions;
  /** Open directly in the compact (minimized) card instead of the full panel */
  startMinimized?: boolean;
  /**
   * Arms the idle auto-collapse timer. Proactive suggestion
   * opens only — an unpinned card collapses to the FAB badge
   * after AUTO_COLLAPSE_MS. Requires startMinimized.
   */
  autoCollapse?: boolean;
}

/**
 * Action methods for the chat dialog (stable — identity never changes).
 */
export interface ChatDialogActions {
  open: (options?: OpenChatOptions) => void;
  /** Open directly in the compact (minimized) card, carrying context/actions. */
  openMinimized: (options?: OpenChatOptions) => void;
  close: () => void;
  /**
   * Dismisses the card. Invariant: a proactive context parks
   * behind the FAB badge (see `collapseToBadge`); every other
   * context closes outright (see `close`).
   */
  dismiss: () => void;
  toggleMinimize: () => void;
  updateContext: (context: Partial<ChatDialogContext>) => void;
  /**
   * Drops the context entirely. `updateContext` merges, so it can only
   * blank fields — leaving a category/resources husk the header still
   * renders and the API still receives.
   */
  clearContext: () => void;
  /**
   * Merges fresh proactive data into a showing proactive card
   * (open or parked); no-op when no proactive context is shown.
   */
  refreshProactiveContext: (updates: Partial<ChatDialogContext>) => void;
  /**
   * Closes the card but keeps the root proactive context parked
   * behind the FAB badge, recoverable via the chat welcome.
   */
  collapseToBadge: () => void;
  /** Cancels the pending auto-collapse (user showed intent). */
  pinCard: () => void;
  /**
   * Parks an OPEN proactive card behind the FAB badge when another
   * surface takes the screen. No-op unless a proactive card is open —
   * never a wizard, a plain chat, or an already-collapsed badge.
   * Returns whether it parked, so callers only reopen the gate for a
   * card they hid.
   */
  parkProactiveCard: () => boolean;
  /**
   * Clears a parked proactive context. A parked suggestion belongs
   * to the route that produced it — call this from the scanner
   * host's unmount cleanup. No-op while the card is open.
   */
  clearParkedSuggestions: () => void;
  /**
   * Opt out of proactive suggestions — strips them so the open panel
   * becomes a plain chat. Backs the header "+" and the card's Skip.
   */
  dismissSuggestions: () => void;
  /**
   * Settled empty scan: clears the suggestion surface. Open card →
   * plain chat; parked badge stays proactive so a later scan can
   * refresh it back. Keeps the conversation pin. Returns whether it
   * emptied a live proactive surface (false = no-op on an opted-out
   * plain chat).
   */
  emptyProactiveScan: () => boolean;
  /**
   * Dismisses a proactive suggestion surface when its context is no
   * longer valid — e.g. the package builder card/badge when the view
   * becomes a signing view. Closes both an open auto-opened card and a
   * parked FAB badge outright (a proactive card is a suggestion surface,
   * not a user-authored chat, so it must not linger as a stripped husk).
   * No-op on a plain (non-proactive) chat, so an opt-out is preserved.
   *
   * Returns true when it closed a proactive surface — the caller may then
   * re-arm a once-per-session gate for reopen-on-return; false on the
   * plain-chat no-op, where re-arming would resurrect the card.
   */
  closeProactiveCard: () => boolean;
  handleQuickAction: (action: ContextQuickAction) => void;
  /**
   * Invoke a registered action callback. Returns whether the action was
   * handled: false when no callback is registered or the callback itself
   * reports an explicit `false` (e.g. the EB bridge skipping while another
   * flow is open). A void return counts as handled.
   */
  handleAction: (actionId: string, context?: EBActionContext) => boolean;
  registerCallback: (key: string, callback: (context?: EBActionContext) => boolean | void) => void;
  unregisterCallback: (key: string) => void;
  startDealBuilder: (options?: DealBuilderOpenOptions) => void;
  exitDealBuilder: () => void;
  startSigningSetup: (options: SigningSetupOpenOptions, launchOptions?: SigningSetupLaunchOptions) => void;
  exitSigningSetup: () => void;
  /** Set a greeting message on the FAB. Click on the card opens chat. */
  setGreeting: (message: string) => void;
  /**
   * Set a toast-style message on the FAB. Same card UI as a greeting, but
   * the body is non-interactive — only the X button (or 7s auto-dismiss)
   * clears it. Pass `{ openChatOnClick: true }` to keep open-on-click.
   */
  setToast: (message: string, options?: {
    openChatOnClick?: boolean;
    action?: ToastActionDescriptor;
    variant?: 'default' | 'success' | 'error';
  }) => void;
  /** Clear the greeting/toast message (shrink FAB back to compact) */
  clearGreeting: () => void;
  /** Register and invoke assistant action handlers by id. */
  registerAssistantActionHandler: (id: string, handler: () => void) => void;
  unregisterAssistantActionHandler: (id: string) => void;
  invokeAssistantAction: (id: string) => void;
  /** Register and invoke toast action handlers by id. */
  registerToastActionHandler: (id: string, handler: () => void) => void;
  unregisterToastActionHandler: (id: string) => void;
  invokeToastAction: (id: string) => void;
  /** Set or clear the PDF text selection context */
  setSelectionContext: (ctx: {
    text: string;
    source: string;
  } | null) => void;
  /**
   * Publishes the current action set into the minimized card's
   * context without changing open/minimized state — refreshing an
   * already-open card live. The opener (e.g. the builder) controls
   * the initial open via openMinimized.
   */
  setMinimizedActions: (message: string, actions: ContextQuickAction[], options?: {
    summaryVariant?: 'default' | 'success' | 'error';
  }) => void;
  /** Pre-register options so the next open() without args uses them */
  setPendingOpenOptions: (options: OpenChatOptions | null) => void;
  /** Ref tracking isOpen — read without subscribing to state changes */
  isOpenRef: React.RefObject<boolean>;
}

/**
 * Chat dialog controller interface (state + actions combined).
 */
export interface ChatDialogController extends ChatDialogActions {
  /** Current dialog state */
  state: ChatDialogState;
}

/**
 * Initial state for the chat dialog
 */
const initialState: ChatDialogState = {
  isOpen: false,
  isMinimized: false,
  context: null,
  suppressComposerAutofocus: false,
  file: null,
  initialMessage: null,
  mentionsContext: [],
  mode: 'chat',
  dealBuilderOptions: undefined,
  dealDocumentOptions: undefined,
  greetingMessage: null,
  greetingOpensChat: true,
  toastAction: null,
  greetingVariant: 'default',
  selectionContext: null,
  sessionEpoch: 0
};
const ChatDialogStateCtx = createContext<ChatDialogState>(initialState);
const ChatDialogActionsCtx = createContext<ChatDialogActions | null>(null);
const ChatDialogCtx = createContext<ChatDialogController | null>(null);

/**
 * Chat Dialog Provider Component
 *
 * Wrap your app with this provider to enable global chat dialog control.
 */
export function ChatDialogProvider({
  children
}: {
  children: ReactNode;
}) {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const pendingOpenOptionsRef = useRef<OpenChatOptions | null>(null);
  const [state, setState] = useState<ChatDialogState>(initialState);
  const callbacksRef = useRef<Record<string, (context?: EBActionContext) => boolean | void>>({});
  const assistantActionHandlersRef = useRef<Record<string, () => void>>({});
  const toastActionHandlersRef = useRef<Record<string, () => void>>({});
  // One-shot return target for signing setup launches from non-chat hosts.
  const signingSetupReturnTargetRef = useRef<(() => void) | null>(null);
  const isOpenRef = useRef(false);
  isOpenRef.current = state.isOpen;
  // Source of truth for sessionEpoch. A ref (not state) because the
  // open actions build one state object literal — reading a prior
  // render's state to compute "prev + 1" isn't available there.
  const sessionEpochRef = useRef(0);
  // Read by dismiss() without depending on `state` — keeps the
  // actions object's identity stable across every state change.
  const stateRef = useRef(state);
  stateRef.current = state;
  const autoCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearAutoCollapseTimer = useCallback(() => {
    if (autoCollapseTimerRef.current !== null) {
      clearTimeout(autoCollapseTimerRef.current);
      autoCollapseTimerRef.current = null;
    }
  }, []);

  // The timer must not outlive this provider instance — the
  // (main) and (main-fullscreen) layouts each mount their own.
  useEffect(() => clearAutoCollapseTimer, [clearAutoCollapseTimer]);

  // Publish whether a wizard owns the dialog so the proactive
  // controller (mounted above this provider) can suppress its card and
  // reopen it reactively when the wizard closes. Reset on unmount so a
  // provider swap (the (main) and (main-fullscreen) layouts each mount
  // their own) can't strand a stale `true` on the module-level flag.
  useEffect(() => {
    setChatWizardOpen(state.isOpen && state.mode !== 'chat');
    return () => setChatWizardOpen(false);
  }, [state.isOpen, state.mode]);

  // Coach mutual exclusion reads/writes the shared coach open
  // state. Mirrored through a ref so the stable open actions can
  // strip it without taking the hook value as a dependency — the
  // actions object's identity must never change.
  const coachOpenState = useOpenState();
  const coachOpenStateRef = useRef(coachOpenState);
  coachOpenStateRef.current = coachOpenState;

  // Mutual exclusion with the Deal Coach panel: opening the chat
  // clears the shared coach open state so CoachPanelMount
  // unmounts. Done imperatively from the open actions rather than
  // a standing isOpen effect, so opening the coach while the chat
  // is open (CoachPanelMount closes the chat) doesn't race a
  // reactive strip that clears the param before the panel mounts.
  const stripCoachParam = useCallback(() => {
    const {
      isOpen,
      closeCoach
    } = coachOpenStateRef.current;
    if (!isOpen) return;
    closeCoach();
  }, []);
  const setPendingOpenOptions = useCallback((options: OpenChatOptions | null) => {
    pendingOpenOptionsRef.current = options;
  }, []);
  const collapseToBadge = useCallback(() => {
    clearAutoCollapseTimer();
    // Restore focus only when the collapse takes it
    // away — an idle collapse must not steal focus
    // from a page input the user is using.
    const focusWasInside = !!document.activeElement?.closest?.(`[${CHAT_SURFACE_ATTR}]`);
    setState(prev => ({
      ...initialState,
      context: prev.context,
      // Keep the epoch aligned with sessionEpochRef while the
      // session provider stays mounted through the collapse —
      // resetting to 0 would misfire its session-reset effect.
      sessionEpoch: prev.sessionEpoch,
      // Parking must not clear another feature's FAB status cue
      // (e.g. a toast set by an unrelated flow while open).
      greetingMessage: prev.greetingMessage,
      greetingOpensChat: prev.greetingOpensChat,
      toastAction: prev.toastAction,
      greetingVariant: prev.greetingVariant
    }));
    if (focusWasInside) {
      requestAnimationFrame(() => {
        previousFocusRef.current?.focus();
        previousFocusRef.current = null;
      });
    }
  }, [clearAutoCollapseTimer]);
  const pinCard = useCallback(() => {
    clearAutoCollapseTimer();
    // The user engaged with the card, so auto-open focus-stealing is
    // no longer a concern — clear the suppression flag it set.
    setState(prev_0 => prev_0.suppressComposerAutofocus ? {
      ...prev_0,
      suppressComposerAutofocus: false
    } : prev_0);
  }, [clearAutoCollapseTimer]);

  // Park an open proactive card behind the FAB badge when another
  // surface (Analyze side-panel, wizard) takes the screen. Guarded so
  // it only touches OUR proactive card — never closes a wizard or a
  // plain user chat. Unmounting the card (isOpen → false) is what makes
  // this safe: its auto-collapse timer and Escape handler stand down,
  // unlike a still-mounted "render null" hide.
  const parkProactiveCard = useCallback((): boolean => {
    const {
      isOpen: isOpen_0,
      context
    } = stateRef.current;
    // Only an OPEN proactive card can be parked. A card already
    // collapsed/dismissed to the badge (isOpen false) must report false
    // so the caller doesn't clear the gate and resurface a full card
    // the user already dismissed.
    if (!isOpen_0 || !context?.proactive) return false;
    collapseToBadge();
    return true;
  }, [collapseToBadge]);
  const open = useCallback((options_0?: OpenChatOptions) => {
    const resolved = options_0 ?? pendingOpenOptionsRef.current ?? undefined;

    // Coach and chat are mutually exclusive; opening chat closes coach.
    stripCoachParam();
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Opening a regular chat cancels stale host-return behavior.
    signingSetupReturnTargetRef.current = null;
    setState({
      isOpen: true,
      isMinimized: resolved?.startMinimized ?? false,
      context: resolved?.context ?? null,
      suppressComposerAutofocus: !!(resolved?.autoCollapse && resolved?.startMinimized),
      file: resolved?.file ?? null,
      initialMessage: resolved?.initialMessage ?? null,
      mentionsContext: resolved?.mentionsContext ?? [],
      mode: resolved?.dealDocumentOptions ? 'deal-document' : resolved?.dealBuilderOptions ? 'deal-builder' : 'chat',
      dealBuilderOptions: resolved?.dealBuilderOptions,
      dealDocumentOptions: resolved?.dealDocumentOptions,
      greetingMessage: null,
      greetingOpensChat: true,
      toastAction: null,
      greetingVariant: 'default',
      selectionContext: null,
      sessionEpoch: ++sessionEpochRef.current
    });
    clearAutoCollapseTimer();
    if (resolved?.autoCollapse && resolved?.startMinimized) {
      autoCollapseTimerRef.current = setTimeout(collapseToBadge, AUTO_COLLAPSE_MS);
    }
  }, [stripCoachParam, clearAutoCollapseTimer, collapseToBadge]);
  const openMinimized = useCallback((options_1?: OpenChatOptions) => {
    open({
      ...options_1,
      startMinimized: true
    });
  }, [open]);
  const startDealBuilder = useCallback((options_2?: DealBuilderOpenOptions) => {
    // Every DEAL launcher passes through here — the Create Deal row, the
    // keyboard path, `onAddressResolved`, the chat chip and the custom-wizard
    // rows — so this is the only place that can report launches without one
    // entry point silently going uncounted and skewing the drop-off.
    //
    // The listing flow shares this function but is a different funnel with no
    // pilot of its own. Counting it would land its sessions on the hardcoded
    // side of the comparison and inflate the very baseline these events exist
    // to measure.
    if (options_2?.flow !== 'listing') {
      trackDealWizardLaunched(options_2?.wizardId, options_2?.target ? {
        target: 'deal_update',
        entry: options_2.target.entry
      } : undefined);
    }
    // Resolve the address/formattedAddress shorthands into initialState.
    // Each copies independently so a formatted-only address (e.g. a chat
    // directive carrying just the display string) still prefills the wizard.
    const resolved_0: DealBuilderOpenOptions | undefined = options_2 ? {
      wizardId: options_2.wizardId,
      // Copied explicitly: this object REPLACES the caller's, so any
      // field omitted here never reaches the panel.
      flow: options_2.flow,
      target: options_2.target,
      initialState: {
        ...options_2.initialState,
        ...(options_2.address && !options_2.initialState?.address ? {
          address: options_2.address
        } : {}),
        ...(options_2.formattedAddress && !options_2.initialState?.formattedAddress ? {
          formattedAddress: options_2.formattedAddress
        } : {}),
        // The listing category's after-create hook takes no arguments,
        // so the folder rides state to reach it.
        ...(options_2.listingParentId && !options_2.initialState?.listingParentId ? {
          listingParentId: options_2.listingParentId
        } : {})
      }
    } : undefined;
    clearAutoCollapseTimer();
    stripCoachParam();
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Deal-builder launches should not inherit a prior signing back target.
    signingSetupReturnTargetRef.current = null;
    setState({
      isOpen: true,
      isMinimized: false,
      context: null,
      suppressComposerAutofocus: false,
      file: null,
      initialMessage: null,
      mentionsContext: [],
      mode: 'deal-builder',
      dealBuilderOptions: resolved_0,
      greetingMessage: null,
      greetingOpensChat: true,
      toastAction: null,
      greetingVariant: 'default',
      selectionContext: null,
      sessionEpoch: ++sessionEpochRef.current
    });
  }, [clearAutoCollapseTimer, stripCoachParam]);
  const exitDealBuilder = useCallback(() => {
    setState(prev_1 => ({
      ...prev_1,
      mode: 'chat',
      dealBuilderOptions: undefined
    }));
  }, []);
  const startSigningSetup = useCallback((options_3: SigningSetupOpenOptions, launchOptions?: SigningSetupLaunchOptions) => {
    clearAutoCollapseTimer();
    stripCoachParam();
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Capture a one-shot host return target before replacing the panel.
    signingSetupReturnTargetRef.current = launchOptions?.onBack ?? null;
    setState({
      isOpen: true,
      isMinimized: false,
      context: null,
      suppressComposerAutofocus: false,
      file: null,
      initialMessage: null,
      mentionsContext: [],
      mode: 'signing-setup',
      signingSetupOptions: options_3,
      greetingMessage: null,
      greetingOpensChat: true,
      toastAction: null,
      greetingVariant: 'default',
      selectionContext: null,
      sessionEpoch: ++sessionEpochRef.current
    });
  }, [clearAutoCollapseTimer, stripCoachParam]);
  const exitSigningSetup = useCallback(() => {
    const returnTarget = signingSetupReturnTargetRef.current;
    // Return targets are single-use so a later Back cannot reopen stale UI.
    signingSetupReturnTargetRef.current = null;
    if (returnTarget) {
      // Close ChatDialog first so the restored host panel owns focus/edge space.
      setState(initialState);
      returnTarget();
      return;
    }
    setState(prev_2 => ({
      ...prev_2,
      mode: 'chat',
      signingSetupOptions: undefined
    }));
  }, []);
  const setGreeting = useCallback((message: string) => {
    setState(prev_3 => ({
      ...prev_3,
      greetingMessage: message,
      greetingOpensChat: true,
      toastAction: null,
      greetingVariant: 'default'
    }));
  }, []);
  const setToast = useCallback((message_0: string, options_4?: {
    openChatOnClick?: boolean;
    action?: ToastActionDescriptor;
    variant?: 'default' | 'success' | 'error';
  }) => {
    setState(prev_4 => ({
      ...prev_4,
      greetingMessage: message_0,
      greetingOpensChat: options_4?.openChatOnClick ?? false,
      toastAction: options_4?.action ?? null,
      greetingVariant: options_4?.variant ?? 'default'
    }));
  }, []);
  const registerAssistantActionHandler = useCallback((id: string, handler: () => void) => {
    assistantActionHandlersRef.current = {
      ...assistantActionHandlersRef.current,
      [id]: handler
    };
  }, []);
  const unregisterAssistantActionHandler = useCallback((id_0: string) => {
    const next = {
      ...assistantActionHandlersRef.current
    };
    delete next[id_0];
    assistantActionHandlersRef.current = next;
  }, []);
  const invokeAssistantAction = useCallback((id_1: string) => {
    assistantActionHandlersRef.current[id_1]?.();
  }, []);
  const registerToastActionHandler = useCallback((id_2: string, handler_0: () => void) => {
    toastActionHandlersRef.current = {
      ...toastActionHandlersRef.current,
      [id_2]: handler_0
    };
  }, []);
  const unregisterToastActionHandler = useCallback((id_3: string) => {
    const next_0 = {
      ...toastActionHandlersRef.current
    };
    delete next_0[id_3];
    toastActionHandlersRef.current = next_0;
  }, []);
  const invokeToastAction = useCallback((id_4: string) => {
    toastActionHandlersRef.current[id_4]?.();
  }, []);
  const clearGreeting = useCallback(() => {
    setState(prev_5 => ({
      ...prev_5,
      greetingMessage: null,
      greetingOpensChat: true,
      toastAction: null,
      greetingVariant: 'default'
    }));
  }, []);
  const setSelectionContext = useCallback((ctx: {
    text: string;
    source: string;
  } | null) => {
    setState(prev_6 => ({
      ...prev_6,
      selectionContext: ctx
    }));
  }, []);
  const setMinimizedActions = useCallback((message_1: string, actions: ContextQuickAction[], options_5?: {
    summaryVariant?: 'default' | 'success' | 'error';
  }) => {
    setState(prev_7 => ({
      ...prev_7,
      context: {
        ...(prev_7.context ?? {
          category: 'workflow-state',
          resources: []
        }),
        summary: message_1,
        summaryVariant: options_5?.summaryVariant ?? 'default',
        quickActions: actions
      }
    }));
  }, []);
  const close = useCallback(() => {
    clearAutoCollapseTimer();
    // A full close cancels any pending signing setup return target.
    signingSetupReturnTargetRef.current = null;
    setState(initialState);
    // Keep pendingOpenOptionsRef intact so the FAB can reopen in the same
    // mode (e.g. deal-document) without the registering effect re-running.

    requestAnimationFrame(() => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    });
  }, [clearAutoCollapseTimer]);

  // Invariant: a proactive context parks behind the FAB badge;
  // everything else closes outright.
  const dismiss = useCallback(() => {
    if (stateRef.current.context?.proactive) {
      collapseToBadge();
    } else {
      close();
    }
  }, [collapseToBadge, close]);
  const toggleMinimize = useCallback(() => {
    // Any minimize/expand toggle is user intent — pin the card and
    // lift the auto-open focus suppression (the full panel has no
    // onInteract hook) so the deferred composer focus can run.
    clearAutoCollapseTimer();
    setState(prev_8 => ({
      ...prev_8,
      isMinimized: !prev_8.isMinimized,
      suppressComposerAutofocus: false
    }));
  }, [clearAutoCollapseTimer]);
  const updateContext = useCallback((updates: Partial<ChatDialogContext>) => {
    setState(prev_9 => ({
      ...prev_9,
      context: prev_9.context ? {
        ...prev_9.context,
        ...updates
      } : updates as ChatDialogContext
    }));
  }, []);
  const clearContext = useCallback(() => {
    setState(prev_10 => prev_10.context ? {
      ...prev_10,
      context: null
    } : prev_10);
  }, []);

  // Guarded proactive-context patch: no-op unless a proactive card is
  // showing (never resurrects a closed/cleared card or touches a
  // plain chat). `produce` returns the fields to merge into `context`,
  // or null to bail without a state change.
  const patchProactiveContext = useCallback((produce: (prev: ChatDialogState) => Partial<ChatDialogContext> | null) => {
    setState(prev_11 => {
      if (!prev_11.context?.proactive) return prev_11;
      const patch = produce(prev_11);
      if (patch === null) return prev_11;
      return {
        ...prev_11,
        context: {
          ...prev_11.context,
          ...patch
        }
      };
    });
  }, []);

  // Refresh live sections/counts when the scanner re-reports after
  // data changes. Fires for a live proactive surface (open card or
  // parked badge) AND for an open card whose sections were stripped on
  // the last route change — the new route's scan repopulates it and
  // clears the stripped marker. A manual plain chat or a chat the user
  // turned plain (Skip / New chat) has neither flag, so it's untouched.
  const refreshProactiveContext = useCallback((updates_0: Partial<ChatDialogContext>) => setState(prev_12 => {
    if (!prev_12.context?.proactive && !prev_12.context?.proactiveStripped) {
      return prev_12;
    }
    return {
      ...prev_12,
      context: {
        ...prev_12.context,
        ...updates_0,
        proactive: true,
        proactiveStripped: false
      }
    };
  }), []);

  // Scanner-host unmount (route change): strip an OPEN card's stale
  // section list, but bail on a closed/parked badge so it survives —
  // its counts are self-contained (no chip callbacks to go stale) and
  // the next route's scanner refreshes them. The stripped OPEN card
  // keeps `proactiveStripped` so that same next-route scan can
  // repopulate it (vs a user opt-out, which stays plain). The
  // conversation stays (session reset keys on sessionEpoch, not
  // context identity).
  const clearParkedSuggestions = useCallback(() => patchProactiveContext(prev_13 => prev_13.isOpen ? {
    proactive: false,
    proactiveStripped: true,
    proactiveSections: undefined,
    summary: undefined,
    quickActions: []
  } : null), [patchProactiveContext]);

  // User opted out (header "+" / card Skip): drop to a plain chat and
  // also clear the deal scope (resources/aiContext/title) an "Ask
  // about it" drill may have pinned, so the chat is genuinely unscoped.
  const dismissSuggestions = useCallback(() => setState(prev_14 => {
    // Fire on any proactive-origin surface — a live card OR one
    // already stripped by a route change (so opting out of a
    // stripped card still sticks). No-op on a manual plain chat.
    if (!prev_14.context?.proactive && !prev_14.context?.proactiveStripped) {
      return prev_14;
    }
    return {
      ...prev_14,
      context: {
        ...prev_14.context,
        proactive: false,
        // A user opt-out is NOT a route strip — clear the marker so
        // the next route's scan can't repopulate the chat they
        // asked to keep plain.
        proactiveStripped: false,
        proactiveSections: undefined,
        summary: undefined,
        quickActions: [],
        resources: [],
        aiContext: undefined,
        title: undefined
      }
    };
  }), []);

  // Settled empty scan — nothing to suggest right now. Clears the
  // suggestion surface but keeps resources/aiContext (an "Ask about
  // it" conversation pin). An OPEN card drops to a plain chat; a
  // CLOSED parked badge stays proactive so a later non-empty scan can
  // refresh it back — otherwise the badge could never recover. Returns
  // whether it emptied a LIVE proactive surface (vs no-op on a chat the
  // user turned plain) so the caller only resets its recovery gate for
  // a genuine empty scan, never for an opt-out.
  const emptyProactiveScan = useCallback((): boolean => {
    const wasLiveProactive = !!stateRef.current.context?.proactive;
    patchProactiveContext(prev_15 => ({
      proactive: !prev_15.isOpen,
      proactiveSections: [],
      summary: undefined,
      quickActions: []
    }));
    return wasLiveProactive;
  }, [patchProactiveContext]);
  const closeProactiveCard = useCallback((): boolean => {
    // Only a proactive surface is ours to dismiss — a plain chat (the user
    // opted out via Skip / New chat, or never had suggestions) is left
    // untouched, and we report no-op so the caller doesn't re-arm the
    // once-per-session gate and resurrect the card.
    if (!stateRef.current.context?.proactive) return false;
    // Close outright — open card OR parked FAB badge. A proactive card is an
    // auto-opened suggestion surface, not a user-authored chat; when its
    // context goes invalid (a signing/docs view) it should disappear, not
    // linger as a stripped plain-chat husk. There is no reliable in-provider
    // signal for "the user built a real conversation here" —
    // suppressComposerAutofocus is a focus flag that a FAB-badge reopen also
    // clears — so we do not try to preserve one; the card re-opens on return
    // to the builder view.
    close();
    return true;
  }, [close]);
  const handleQuickAction = useCallback((action: ContextQuickAction) => {
    switch (action.type) {
      case 'navigate':
        if (action.href) {
          close();
          window.location.href = action.href;
        }
        break;
      case 'prompt':
        if (action.prompt) {
          setState(prev_16 => ({
            ...prev_16,
            initialMessage: action.prompt ?? null
          }));
        }
        break;
    }
  }, [close]);

  /**
   * Handle an exit action by calling the registered callback
   */
  const handleAction = useCallback((actionId: string, context_0?: EBActionContext): boolean => {
    const callback = callbacksRef.current[actionId];
    if (callback) {
      // Explicit `false` from a callback means "not handled" (e.g. the EB
      // bridge skipping while another flow is open); void counts as handled.
      return callback(context_0) !== false;
    }
    // Log info so we can troubleshoot unregistered chat actions
    logInfo('No handler registered for action', {
      actionId
    });
    return false;
  }, []);
  const registerCallback = useCallback((key: string, callback_0: (context?: EBActionContext) => boolean | void) => {
    callbacksRef.current = {
      ...callbacksRef.current,
      [key]: callback_0
    };
  }, []);
  const unregisterCallback = useCallback((key_0: string) => {
    const next_1 = {
      ...callbacksRef.current
    };
    delete next_1[key_0];
    callbacksRef.current = next_1;
  }, []);

  // Stable actions object — all methods are useCallbacks with stable deps,
  // so this useMemo never recomputes after the initial render.
  const actions_0 = useMemo((): ChatDialogActions => ({
    open,
    openMinimized,
    close,
    dismiss,
    toggleMinimize,
    updateContext,
    clearContext,
    refreshProactiveContext,
    collapseToBadge,
    pinCard,
    parkProactiveCard,
    clearParkedSuggestions,
    dismissSuggestions,
    emptyProactiveScan,
    closeProactiveCard,
    handleQuickAction,
    handleAction,
    registerCallback,
    unregisterCallback,
    startDealBuilder,
    exitDealBuilder,
    startSigningSetup,
    exitSigningSetup,
    setGreeting,
    setToast,
    clearGreeting,
    registerAssistantActionHandler,
    unregisterAssistantActionHandler,
    invokeAssistantAction,
    registerToastActionHandler,
    unregisterToastActionHandler,
    invokeToastAction,
    setSelectionContext,
    setMinimizedActions,
    setPendingOpenOptions,
    isOpenRef
  }), [open, openMinimized, close, dismiss, toggleMinimize, updateContext, clearContext, refreshProactiveContext, collapseToBadge, pinCard, parkProactiveCard, clearParkedSuggestions, dismissSuggestions, emptyProactiveScan, closeProactiveCard, handleQuickAction, handleAction, registerCallback, unregisterCallback, startDealBuilder, exitDealBuilder, startSigningSetup, exitSigningSetup, setGreeting, setToast, clearGreeting, registerAssistantActionHandler, unregisterAssistantActionHandler, invokeAssistantAction, registerToastActionHandler, unregisterToastActionHandler, invokeToastAction, setSelectionContext, setMinimizedActions, setPendingOpenOptions]);

  // Combined controller for backward-compat consumers using useChatDialog().
  const controller = useMemo((): ChatDialogController => ({
    state,
    ...actions_0
  }), [state, actions_0]);
  return <ChatDialogActionsCtx.Provider value={actions_0} data-sentry-element="ChatDialogActionsCtx.Provider" data-sentry-component="ChatDialogProvider" data-sentry-source-file="chat-dialog-provider.tsx">
      <ChatDialogStateCtx.Provider value={state} data-sentry-element="ChatDialogStateCtx.Provider" data-sentry-source-file="chat-dialog-provider.tsx">
        <ChatDialogCtx.Provider value={controller} data-sentry-element="ChatDialogCtx.Provider" data-sentry-source-file="chat-dialog-provider.tsx">
          {children}
        </ChatDialogCtx.Provider>
      </ChatDialogStateCtx.Provider>
    </ChatDialogActionsCtx.Provider>;
}

/**
 * Hook to access the chat dialog controller (state + actions).
 * Re-renders on every state change. Prefer useChatDialogActions() when
 * you only need to call methods (open/close/startDealBuilder/etc.).
 */
export function useChatDialog(): ChatDialogController {
  const controller = use(ChatDialogCtx);
  if (!controller) {
    throw new Error('useChatDialog must be used within a ChatDialogProvider');
  }
  return controller;
}

/**
 * Hook for stable action methods only. Does NOT re-render on state changes.
 * Use this in hooks/components that only need to call open/close/etc.
 */
export function useChatDialogActions(): ChatDialogActions {
  const actions = use(ChatDialogActionsCtx);
  if (!actions) {
    throw new Error('useChatDialogActions must be used within a ChatDialogProvider');
  }
  return actions;
}

/**
 * Like `useChatDialogActions` but returns `null` when called outside a
 * provider instead of throwing. Use for code paths that must work in test
 * environments without a `ChatDialogProvider` wrapper (e.g. shared toast
 * helpers consumed by widely-tested components).
 */
export function useChatDialogActionsSafe(): ChatDialogActions | null {
  return use(ChatDialogActionsCtx);
}

/**
 * Hook for chat dialog state only. Re-renders when state changes.
 */
export function useChatDialogState(): ChatDialogState {
  return use(ChatDialogStateCtx);
}

/**
 * Hook to check if chat dialog is available (inside provider)
 */
export function useChatDialogAvailable(): boolean {
  const controller = use(ChatDialogCtx);
  return controller !== null;
}